import { useRef } from 'react';
import {
  AppShell,
  makeSsoAppNavigation,
  type NavItem,
  type SsoAppNavigation,
} from '@kawai/app-shell';
import {
  buildSsoSessionKey,
  resolveHeaderAppLinks,
  resolvePortalUrl,
  type SsoSessionInput,
} from '@kawai/shared';
import { useAuth } from '../lib/auth.js';
import { useTenantBrand } from '../lib/tenantBrand.js';
import { callIssueSsoToken } from '../lib/api/issueSsoToken.js';
import { ROUTES } from '../routes.js';

const NAV_ITEMS: readonly NavItem[] = [
  { to: ROUTES.fuelPrices, label: '燃料価格', end: true },
  // F-16: 一覧表の区分 / 派生価格の値引き幅のテナント設定
  { to: ROUTES.settings, label: '設定' },
] as const;

/**
 * F-02 / T-10: tenant-fuel app の共通レイアウト。
 *
 * 共通シェル `@kawai/app-shell` の `<AppShell>` に NAV_ITEMS と app 固有の DI を渡す
 * 薄い wrapper。将来（履歴詳細 / レポート 等）に備えて NAV_ITEMS に追加する形を想定。
 *
 * A-37: 旧「ポータルへ」ボタンを廃止し、契約済みの各管理画面（予約 / 燃料 / 販促）への
 * 直接リンクをヘッダーに表示する（`resolveManagementAppLinks`）。
 */
export function Layout() {
  const { user, role, tenantId, features, signOut } = useAuth();
  // S-10: テナントブランド（brandName / logoUrl / primaryColor）を sticky ヘッダに表示。
  // Layout は AuthGuard 配下で 1 度だけマウントされるため fetch は起動時 1 回のみ。
  const { brand } = useTenantBrand(tenantId);

  // S-91: 現セッションを毎 render で ref に反映し、navigation の getSessionKey が最新の
  // uid + claims fingerprint を読めるようにする（トークンキャッシュ・素リンク確立記録の
  // 両方がこのキー一致で守られ、サインアウト / ユーザー切替 / impersonation を跨いだ再利用を防ぐ）。
  const sessionRef = useRef<SsoSessionInput>({
    uid: null,
    role: null,
    tenantId: null,
    features: [],
  });
  sessionRef.current = { uid: user?.uid ?? null, role, tenantId, features };
  const navRef = useRef<SsoAppNavigation | null>(null);
  if (navRef.current === null) {
    navRef.current = makeSsoAppNavigation(
      (targetApp, { prefetch }) => callIssueSsoToken({ targetApp, prefetch }),
      { getSessionKey: () => buildSsoSessionKey(sessionRef.current) },
    );
  }
  const ssoNavigation = navRef.current;
  // M-164: 「イベント」（promo の /event/status）を顧客/販促の右に並べる。
  const appLinks = resolveHeaderAppLinks(features, 'fuel', import.meta.env.MODE);

  return (
    <AppShell
      navItems={NAV_ITEMS}
      brand={brand}
      appLinks={appLinks}
      onNavigateApp={ssoNavigation.onNavigateApp}
      onPrefetchApp={ssoNavigation.onPrefetchApp}
      prerenderUrls={ssoNavigation.selectPrerenderUrls(appLinks)}
      portalUrl={resolvePortalUrl(import.meta.env.MODE)}
      onSignOut={() => {
        ssoNavigation.clearAllEstablished();
        void signOut();
      }}
    />
  );
}
